import { DomainPreferences, SQLFilters } from '../../../../api/types';
import {
  EmployeeCohortMetricId,
  RegularMetricId,
} from '../../../../api/types-graphql';
import {
  ChartGridConfig,
  KpiBenchmarkConfig,
  KpiFactboxBenchmarkPeriods,
  KpiFactboxBenchmarkTypes,
  KpiV2ComponentConfig,
  KpiV2Type,
  LanguageString,
  LineChartComponentConfig,
  TimePeriodBarChartComponentConfig,
  TimePeriodBarChartQueryConfig,
} from '../../../../common/components/dashboards/types';
import { Months } from '../../../../common/components/timeslider/types';
import { Languages } from '../../../../constants';
import {
  DataFieldWithDataType,
  DataTypes,
  EmployeeDataFields,
  FinancialYearSingleValueByYearsTimeSelection,
  SingleValueTimeSelection,
  TimeSelectionType,
} from '../../../../types';
import { getKeyFromDataFieldWithDataType } from '../../../../utils';
import {
  addQuarter,
  getFiscalQuarter,
  getFiscalYear,
  getMomentDate,
} from '../../../../utils-date';
import { StoryboardDateUtils } from '../../utils';

type SingleMetricFactboxConfig = {
  metricId: RegularMetricId;
  title: LanguageString;
  footer?: LanguageString;
  timeSelection: SingleValueTimeSelection;
  segmentationDataField?: EmployeeDataFields;
  benchmarkConfigs?: KpiBenchmarkConfig[];
  chartGridConfig: ChartGridConfig;
};

const getLastFinYearTimeSelection = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): FinancialYearSingleValueByYearsTimeSelection => {
  const { fiscalYear } = storyboardDateUtils;
  return {
    type: TimeSelectionType.FinancialYearSingleValueByYears,
    input: {
      start: fiscalYear(domainPreferences) - 1,
      end: fiscalYear(domainPreferences) - 1,
    },
  };
};

export const getTurnoverByDimensionChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  dimension: DataFieldWithDataType,
  title: LanguageString,
  filters: SQLFilters | null,
  gridConfig: ChartGridConfig
): TimePeriodBarChartComponentConfig => {
  const {
    getFromDate_EndOfMonth,
    getFromDate_TwelveMonthsAgoEndOfMonth,
    lastMonth,
  } = storyboardDateUtils;
  const lastMonthEndOfMonth = getFromDate_EndOfMonth(lastMonth);
  const twelveMonthsAgoFromLastMonthEndOfMonth =
    getFromDate_TwelveMonthsAgoEndOfMonth(lastMonth);
  const query: TimePeriodBarChartQueryConfig = {
    metrics: [
      {
        type: 'RegularMetricId',
        value: RegularMetricId.Pm0318_3CustomAttritionNikkiTtm,
      },
      {
        type: 'RegularMetricId',
        value: RegularMetricId.Pm0318_4CustomAttritionNikkiLeavers,
      },
    ],
    timeSelection: {
      type: TimeSelectionType.CalendarYearSingleValueByMonths,
      input: {
        start: twelveMonthsAgoFromLastMonthEndOfMonth,
        end: lastMonthEndOfMonth,
      },
    },
    segmentations: [
      {
        type: 'non-hierarchical',
        dataField: dimension,
      },
    ],
    filters: filters ?? undefined,
  };
  return {
    chartId: `by-dimension-chart-${getKeyFromDataFieldWithDataType(dimension)}`,
    chartTypeConfig: {
      chartType: 'bar',
      timeframe: 'timePeriod',
    },
    title,
    queries: [query],
    ...gridConfig,
  };
};

export const getGrossProfitPerHeadOverTimeChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): LineChartComponentConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  const nowDate = getMomentDate(nowEndOfMonth).valueOf();
  const threeQuartersAgo = addQuarter(
    getMomentDate(nowEndOfMonth).valueOf(),
    -3
  );
  const finYearStartMonth =
    domainPreferences.finYearStartMonth ?? Months.January;
  const currentQuarter = {
    quarterOfYear: getFiscalQuarter(nowDate, finYearStartMonth),
    year: getFiscalYear(nowDate, finYearStartMonth),
  };
  const threeQuartersAgoQuarter = {
    quarterOfYear: getFiscalQuarter(threeQuartersAgo, finYearStartMonth),
    year: getFiscalYear(threeQuartersAgo, finYearStartMonth),
  };
  const yoyBenchmarkNowQuarter = {
    quarterOfYear: currentQuarter.quarterOfYear,
    year: currentQuarter.year - 1,
  };
  const yoyBenchmarkThreeQuartersAgoQuarter = {
    quarterOfYear: threeQuartersAgoQuarter.quarterOfYear,
    year: threeQuartersAgoQuarter.year - 1,
  };
  return {
    chartId: 'gross-profit-per-head-chart',
    chartTypeConfig: {
      chartType: 'line',
    },
    title: {
      [Languages.EN]: 'Gross Profit Per Head (Consolidated)',
      [Languages.JA]: '1人あたり粗利（全社） ',
    },
    queries: [
      {
        xAxisId: 0,
        metrics: [
          {
            type: 'EmployeeCohortMetricId',
            value: EmployeeCohortMetricId.Pm0453_1GrossProfitMonthlyPerIndiv,
          },
        ],
        timeSelection: {
          type: TimeSelectionType.FinancialYearQuarterly,
          input: {
            start: threeQuartersAgoQuarter,
            end: currentQuarter,
          },
        },
      },
      {
        isBenchmark: true,
        xAxisId: 1,
        metrics: [
          {
            type: 'EmployeeCohortMetricId',
            value: EmployeeCohortMetricId.Pm0453_1GrossProfitMonthlyPerIndiv,
          },
        ],
        timeSelection: {
          type: TimeSelectionType.FinancialYearQuarterly,
          input: {
            start: yoyBenchmarkThreeQuartersAgoQuarter,
            end: yoyBenchmarkNowQuarter,
          },
        },
      },
    ],
    gridRowSpan: 1,
    gridColSpan: 1,
  };
};

export const getKeyTalentAttritionOverTimeChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): LineChartComponentConfig => {
  const { nowEndOfMonth, twelveMonthsAgoFromNowEndOfMonth } =
    storyboardDateUtils;
  return {
    chartId: 'key-talent-attrition-overtime-chart',
    chartTypeConfig: {
      chartType: 'line',
    },
    title: {
      [Languages.EN]: 'Key Talent Attrition (Consolidated)',
      [Languages.JA]: 'ハイパフォーマー離職率（事業部別）',
    },
    queries: [
      {
        metrics: [
          {
            type: 'RegularMetricId',
            value: RegularMetricId.Pm0080_3RegretAttritionTtm,
          },
        ],
        timeSelection: {
          type: TimeSelectionType.CalendarYearMonthly,
          input: {
            start: twelveMonthsAgoFromNowEndOfMonth,
            end: nowEndOfMonth,
          },
        },
        segmentations: [
          {
            type: 'non-hierarchical',
            dataField: {
              dataType: DataTypes.EMPLOYEE,
              dataField: EmployeeDataFields.ORGANIZATION_LEVEL_1,
            },
          },
        ],
        filters: `EMPLOYEE.CUSTOM_FILTER_8 = '1' OR EMPLOYEE.CUSTOM_FILTER_8 = '2'`,
      },
    ],
    gridRowSpan: 1,
    gridColSpan: 1,
  };
};

export const getGrossProfitPerHeadByOrganizationChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): TimePeriodBarChartComponentConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  const nowDate = getMomentDate(nowEndOfMonth).valueOf();
  const finYearStartMonth =
    domainPreferences.finYearStartMonth ?? Months.January;
  const currentQuarter = {
    quarterOfYear: getFiscalQuarter(nowDate, finYearStartMonth),
    year: getFiscalYear(nowDate, finYearStartMonth),
  };
  return {
    chartId: 'gross-profit-per-head-by-organization-chart',
    chartTypeConfig: {
      chartType: 'bar',
      timeframe: 'timePeriod',
    },
    title: {
      [Languages.EN]: 'Gross Profit Per Head (By Business Segment)',
      [Languages.JA]: '1人あたり粗利（事業部別）',
    },
    queries: [
      {
        metrics: [
          {
            type: 'EmployeeCohortMetricId',
            value: EmployeeCohortMetricId.Pm0453_1GrossProfitMonthlyPerIndiv,
          },
        ],
        timeSelection: {
          type: TimeSelectionType.FinancialYearSingleValueByQuarters,
          input: {
            start: currentQuarter,
            end: currentQuarter,
          },
        },
        segmentations: [
          {
            type: 'non-hierarchical',
            dataField: {
              dataType: DataTypes.EMPLOYEE,
              dataField: EmployeeDataFields.ORGANIZATION_LEVEL_1,
            },
          },
        ],
      },
    ],
  };
};

export const getKeyTalentAttritionByOrganizationChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): TimePeriodBarChartComponentConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  const nowDate = getMomentDate(nowEndOfMonth).valueOf();
  const finYearStartMonth =
    domainPreferences.finYearStartMonth ?? Months.January;
  const currentQuarter = {
    quarterOfYear: getFiscalQuarter(nowDate, finYearStartMonth),
    year: getFiscalYear(nowDate, finYearStartMonth),
  };
  return {
    chartId: 'key-talent-attrition-by-organization-chart',
    chartTypeConfig: {
      chartType: 'bar',
      timeframe: 'timePeriod',
    },
    title: {
      [Languages.EN]: 'Key Talent Attrition (By Business Segment)',
      [Languages.JA]: 'ハイパフォーマー分布（事業部別）',
    },
    queries: [
      {
        metrics: [
          {
            type: 'RegularMetricId',
            value: RegularMetricId.Pm0001_1IndividualsCount,
          },
        ],
        timeSelection: {
          type: TimeSelectionType.FinancialYearSingleValueByQuarters,
          input: {
            start: currentQuarter,
            end: currentQuarter,
          },
        },
        segmentations: [
          {
            type: 'non-hierarchical',
            dataField: {
              dataType: DataTypes.EMPLOYEE,
              dataField: EmployeeDataFields.ORGANIZATION_LEVEL_1,
            },
          },
          {
            type: 'non-hierarchical',
            dataField: {
              dataType: DataTypes.EMPLOYEE,
              dataField: EmployeeDataFields.CUSTOM_FILTER_8,
            },
          },
        ],
      },
    ],
  };
};

export const getWorkforceFulfillmentRateFactboxConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): KpiV2ComponentConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  return {
    chartId: `${EmployeeCohortMetricId.Pm0286_2PlannedIndividualsPercHc}-factbox`,
    chartTypeConfig: {
      chartType: 'kpi-v2',
    },
    title: {
      [Languages.EN]: '[Org Agility] Workforce Fulfillment Rate',
      [Languages.JA]: '[変革実行力] 人材ポートフォリオ充足率',
    },
    subtitle: {
      [Languages.EN]: '(%)',
      [Languages.JA]: '(%)',
    },
    options: {
      kpiType: KpiV2Type.SINGLE_METRIC,
      section: {
        key: EmployeeCohortMetricId.Pm0286_2PlannedIndividualsPercHc,
        query: {
          metrics: [
            {
              type: 'EmployeeCohortMetricId',
              value: EmployeeCohortMetricId.Pm0286_2PlannedIndividualsPercHc,
            },
          ],
          timeSelection: {
            type: TimeSelectionType.CalendarYearSingleValueByMonths,
            input: { start: nowEndOfMonth, end: nowEndOfMonth },
          },
          segmentations: [
            {
              type: 'non-hierarchical',
              dataField: {
                dataType: DataTypes.EMPLOYEE,
                dataField: EmployeeDataFields.ORGANIZATION_LEVEL_1,
              },
            },
          ],
        },
        benchmarkConfigs: [
          {
            benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
            benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
          },
          {
            benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
            benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
          },
        ],
      },
    },
    gridRowSpan: 1,
    gridColSpan: 1,
  };
};

export const getTenureInJobGradeFactboxConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): KpiV2ComponentConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  return {
    chartId: `${RegularMetricId.Pm0163TenureInJobgradeAvg}-factbox`,
    chartTypeConfig: {
      chartType: 'kpi-v2',
    },
    title: {
      [Languages.EN]: '[Org Agility] Tenure in Jobgrade',
      [Languages.JA]: '[変革実行力] 同一グレードでの滞在期間',
    },
    subtitle: {
      [Languages.EN]: '(◯年◯ヶ月）',
      [Languages.JA]: '(◯年◯ヶ月）',
    },
    options: {
      kpiType: KpiV2Type.SINGLE_METRIC,
      section: {
        key: RegularMetricId.Pm0163TenureInJobgradeAvg,
        query: {
          metrics: [
            {
              type: 'RegularMetricId',
              value: RegularMetricId.Pm0163TenureInJobgradeAvg,
            },
          ],
          timeSelection: {
            type: TimeSelectionType.CalendarYearSingleValueByMonths,
            input: { start: nowEndOfMonth, end: nowEndOfMonth },
          },
          segmentations: [
            {
              type: 'non-hierarchical',
              dataField: {
                dataType: DataTypes.EMPLOYEE,
                dataField: EmployeeDataFields.JOB_GRADE_LEVEL_1,
              },
              filters: ['G1', 'G2', 'G3', 'G4', 'G5', 'G6', 'G7'],
            },
          ],
        },
        benchmarkConfigs: [
          {
            benchmarkPeriod: KpiFactboxBenchmarkPeriods.YEAR_ON_YEAR,
            benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
          },
          {
            benchmarkPeriod: KpiFactboxBenchmarkPeriods.PRIOR_PERIOD,
            benchmarkType: KpiFactboxBenchmarkTypes.DELTA,
          },
        ],
      },
    },
    gridRowSpan: 1,
    gridColSpan: 1,
  };
};

export const getRemoteWorkDayOverTimeChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): LineChartComponentConfig => {
  const { nowEndOfMonth, twelveMonthsAgoFromNowEndOfMonth } =
    storyboardDateUtils;
  return {
    chartId: 'remote-work-day-overtime-chart',
    chartTypeConfig: {
      chartType: 'line',
    },
    title: {
      [Languages.EN]: '[Flexibility] Remote Work Days',
      [Languages.JA]: '[柔軟性] リモートワーク日数',
    },
    subtitle: {
      [Languages.EN]: '（月間、日）',
      [Languages.JA]: '（月間、日）',
    },
    queries: [
      {
        metrics: [
          {
            type: 'RegularMetricId',
            value: RegularMetricId.Pm0219_3DaysRemoteAvg,
          },
        ],
        timeSelection: {
          type: TimeSelectionType.CalendarYearMonthly,
          input: {
            start: twelveMonthsAgoFromNowEndOfMonth,
            end: nowEndOfMonth,
          },
        },
        segmentations: [
          {
            type: 'non-hierarchical',
            dataField: {
              dataType: DataTypes.EMPLOYEE,
              dataField: EmployeeDataFields.FUNCTION_LEVEL_1,
            },
          },
        ],
      },
    ],
    gridRowSpan: 1,
    gridColSpan: 1,
  };
};

export const getOvertimeWorkOverTimeChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): LineChartComponentConfig => {
  const { nowEndOfMonth, twelveMonthsAgoFromNowEndOfMonth } =
    storyboardDateUtils;
  return {
    chartId: 'overtime-work-overtime-chart',
    chartTypeConfig: {
      chartType: 'line',
    },
    title: {
      [Languages.EN]: '[Flexibility] Overtime',
      [Languages.JA]: '[柔軟性] 残業時間',
    },
    subtitle: {
      [Languages.EN]: '（月間、時間）',
      [Languages.JA]: '（月間、時間）',
    },
    queries: [
      {
        metrics: [
          {
            type: 'RegularMetricId',
            value: RegularMetricId.Pm0215_2MonthlyOtSumPerHcWithOt,
          },
        ],
        timeSelection: {
          type: TimeSelectionType.CalendarYearMonthly,
          input: {
            start: twelveMonthsAgoFromNowEndOfMonth,
            end: nowEndOfMonth,
          },
        },
        segmentations: [
          {
            type: 'non-hierarchical',
            dataField: {
              dataType: DataTypes.EMPLOYEE,
              dataField: EmployeeDataFields.FUNCTION_LEVEL_1,
            },
          },
        ],
      },
    ],
    gridRowSpan: 1,
    gridColSpan: 1,
  };
};

export const getTrainingInvestmentPerHeadByOrganizationChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): TimePeriodBarChartComponentConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  const nowDate = getMomentDate(nowEndOfMonth).valueOf();
  const finYearStartMonth =
    domainPreferences.finYearStartMonth ?? Months.January;
  const currentFinYear = getFiscalYear(nowDate, finYearStartMonth);
  return {
    chartId: 'training-investment-per-head-by-organization-chart',
    chartTypeConfig: {
      chartType: 'bar',
      timeframe: 'timePeriod',
    },
    title: {
      [Languages.EN]: '[Long-term Vision] Training Investment Per Head',
      [Languages.JA]: '[長期志向] 人材育成投資額',
    },
    queries: [
      {
        metrics: [
          {
            type: 'EmployeeCohortMetricId',
            value: EmployeeCohortMetricId.Pm0455_1TrainingCostMonthlyPerIndiv,
          },
        ],
        timeSelection: {
          type: TimeSelectionType.FinancialYearSingleValueByYears,
          input: {
            start: currentFinYear,
            end: currentFinYear,
          },
        },
        segmentations: [
          {
            type: 'non-hierarchical',
            dataField: {
              dataType: DataTypes.EMPLOYEE,
              dataField: EmployeeDataFields.ORGANIZATION_LEVEL_1,
            },
          },
        ],
      },
    ],
  };
};

export const getTrainingUtilizationByOrganizationChartConfig = (
  storyboardDateUtils: StoryboardDateUtils,
  domainPreferences: DomainPreferences
): TimePeriodBarChartComponentConfig => {
  const { nowEndOfMonth } = storyboardDateUtils;
  const nowDate = getMomentDate(nowEndOfMonth).valueOf();
  const finYearStartMonth =
    domainPreferences.finYearStartMonth ?? Months.January;
  const currentFinYear = getFiscalYear(nowDate, finYearStartMonth);
  return {
    chartId: 'training-utilization-by-organization-chart',
    chartTypeConfig: {
      chartType: 'bar',
      timeframe: 'timePeriod',
    },
    title: {
      [Languages.EN]: '[Long-term Vision] Training Utilization Rate',
      [Languages.JA]: '[長期志向] 研修受講率',
    },
    queries: [
      {
        metrics: [
          {
            type: 'RegularMetricId',
            value: RegularMetricId.Pm0173_2TraineePercHc,
          },
        ],
        timeSelection: {
          type: TimeSelectionType.FinancialYearSingleValueByYears,
          input: {
            start: currentFinYear,
            end: currentFinYear,
          },
        },
        segmentations: [
          {
            type: 'non-hierarchical',
            dataField: {
              dataType: DataTypes.EMPLOYEE,
              dataField: EmployeeDataFields.ORGANIZATION_LEVEL_1,
            },
          },
        ],
      },
    ],
  };
};
